import { useState } from "react";
import api from "../utils/api/axiosInstance";
import {
  persistHomeChatMatch,
  readHomeChatMatchCache,
} from "../utils/homeChatMatchPersistence";
import {
  clearRememberedGuestChoice,
  rememberGuestChoice,
} from "../utils/guestIntent";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function writeChoiceCache(choice, ownerUserId) {
  const cached = readHomeChatMatchCache(ownerUserId ?? null);
  if (!cached?.products?.length) return;
  persistHomeChatMatch({
    sessionId: cached.sessionId,
    products: cached.products,
    searchParams: cached.searchParams,
    matchProfile: cached.matchProfile,
    resultKey: cached.resultKey,
    ownerUserId: ownerUserId ?? null,
    choice,
  });
}

/**
 * Set, change, clear, and share the decision on the current match session.
 * Guests are not sent to login. A 404 is retried twice because the match
 * session row can land a moment after the shortlist renders.
 */
export default function useDecisionChoice({
  sessionId,
  ownerUserId = null,
  isGuest = false,
} = {}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [shareNotice, setShareNotice] = useState("");

  const setChoice = async ({ productId, tradeoff, reason }) => {
    if (!sessionId) {
      setError("This match is still saving. Try again in a moment.");
      return null;
    }

    setSaving(true);
    setError(null);
    const payload = { productId, tradeoff: tradeoff || "", reason };

    if (isGuest) {
      rememberGuestChoice({
        sessionId,
        productId,
        tradeoff: payload.tradeoff,
        reason,
        pickedAt: new Date().toISOString(),
      });
    }

    let lastError = null;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        const res = await api.put(
          `/analytics/sessions/${sessionId}/choice`,
          payload,
        );
        const choice = res.data?.activeMatch?.choice || res.data?.choice || null;
        writeChoiceCache(choice, ownerUserId);
        if (isGuest && choice?.productId) {
          rememberGuestChoice({
            sessionId,
            productId: choice.productId,
            tradeoff: choice.tradeoff,
            reason: choice.reason,
            pickedAt: choice.pickedAt,
          });
        }
        setSaving(false);
        return res.data;
      } catch (err) {
        lastError = err;
        if (err?.response?.status !== 404 || attempt === 2) break;
        await sleep(attempt === 0 ? 400 : 800);
      }
    }

    setSaving(false);
    setError(
      lastError?.response?.data?.message || "Could not save this decision.",
    );
    return null;
  };

  const clearChoice = async () => {
    if (!sessionId) return null;
    setSaving(true);
    setError(null);
    try {
      const res = await api.delete(`/analytics/sessions/${sessionId}/choice`);
      writeChoiceCache(null, ownerUserId);
      if (isGuest) clearRememberedGuestChoice();
      setSaving(false);
      return res.data;
    } catch (err) {
      setSaving(false);
      setError(err?.response?.data?.message || "Could not clear this decision.");
      return null;
    }
  };

  const shareChoice = async () => {
    if (!sessionId) return null;
    setShareNotice("");
    setError(null);
    try {
      const res = await api.post(
        `/analytics/sessions/${sessionId}/choice/share`,
      );
      const path = res.data?.path;
      if (!path) return null;
      const url = `${window.location.origin}${path}`;
      try {
        await navigator.clipboard.writeText(url);
        setShareNotice("Link copied");
      } catch {
        setShareNotice(url);
      }
      const cached = readHomeChatMatchCache(ownerUserId ?? null);
      if (cached?.choice) {
        writeChoiceCache({ ...cached.choice, sharePath: path }, ownerUserId);
      }
      return url;
    } catch (err) {
      setError(err?.response?.data?.message || "Could not create a share link.");
      return null;
    }
  };

  const applyChoiceResponse = (res) => {
    const choice = res.data?.activeMatch?.choice || res.data?.choice || null;
    if (choice) writeChoiceCache(choice, ownerUserId);
    return choice;
  };

  const watchPrice = async () => {
    if (!sessionId) return null;
    setSaving(true);
    setError(null);
    try {
      const res = await api.post(`/analytics/sessions/${sessionId}/choice/watch`);
      setSaving(false);
      return applyChoiceResponse(res);
    } catch (err) {
      setSaving(false);
      setError(err?.response?.data?.message || "Could not watch this price.");
      return null;
    }
  };

  const stopWatch = async () => {
    if (!sessionId) return null;
    setSaving(true);
    setError(null);
    try {
      const res = await api.delete(`/analytics/sessions/${sessionId}/choice/watch`);
      setSaving(false);
      return applyChoiceResponse(res);
    } catch (err) {
      setSaving(false);
      setError(err?.response?.data?.message || "Could not stop watching.");
      return null;
    }
  };

  const answerFollowUp = async ({ answer, otherProductId }) => {
    if (!sessionId) return null;
    setSaving(true);
    setError(null);
    try {
      const res = await api.post(
        `/analytics/sessions/${sessionId}/choice/follow-up`,
        { answer, otherProductId: otherProductId || undefined },
      );
      setSaving(false);
      return applyChoiceResponse(res);
    } catch (err) {
      setSaving(false);
      setError(err?.response?.data?.message || "Could not save that answer.");
      return null;
    }
  };

  return {
    setChoice,
    clearChoice,
    shareChoice,
    watchPrice,
    stopWatch,
    answerFollowUp,
    saving,
    error,
    shareNotice,
  };
}
